import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { card } from "@/components/ds/styles";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import type { ComponentType, ReactNode } from "react";
import {
  BookOpen,
  ChevronRight,
  Compass,
  Globe,
  Instagram,
  MessageCircle,
  Youtube,
  Facebook,
} from "lucide-react";

import avatarAsset from "@/assets/dr-sara-avatar.jpg.asset.json";
import { publicSiteSettingsQuery } from "@/routes/_public/route";
import { SOCIAL } from "@/lib/social";
import { TikTokIcon, ThreadsIcon } from "@/components/public/social-icons";
import { canonicalLink, canonicalMeta, ogImageMeta } from "@/lib/seo";
import {
  LINKS_COPY,
  LINKS_WHATSAPP_MESSAGE,
  linksWhatsappHref,
  type LinksLang,
} from "@/lib/links-page";

const searchSchema = z.object({
  lang: z.enum(["ar", "en"]).default("ar").catch("ar"),
});

export const Route = createFileRoute("/links")({
  validateSearch: searchSchema,
  // Re-run the loader when `lang` changes so `head()` (title/description/OG)
  // stays in sync with the active language on a search-only navigation —
  // not just on the initial SSR.
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  // Public contact settings are cached and memoized server-side, so SSR keeps
  // the page light for Instagram's in-app browser. No auth, no session, no
  // Supabase client on the page.
  loader: async ({ context, deps }) => {
    const settings = await context.queryClient.ensureQueryData(
      publicSiteSettingsQuery(),
    );
    return { lang: deps.lang, settings };
  },
  head: ({ loaderData }) => {
    const lang: LinksLang = loaderData?.lang ?? "ar";
    const copy = LINKS_COPY[lang];
    return {
      meta: [
        { title: copy.title },
        { name: "description", content: copy.description },
        { property: "og:title", content: copy.title },
        { property: "og:description", content: copy.description },
        { property: "og:type", content: "website" },
        { name: "robots", content: "noindex, follow" },
        ...canonicalMeta("/links"),
        ...ogImageMeta(),
      ],
      links: [canonicalLink("/links")],
    };
  },
  component: LinksPage,
});

function useLang(): LinksLang {
  const search = useSearch({ from: "/links" });
  return search.lang;
}

function LinksPage() {
  const lang = useLang();
  // Loader prefetched settings; useQuery keeps it non-suspending so a settings
  // hiccup never blanks the bio page.
  const loaderData = Route.useLoaderData();
  const { data: settings } = useQuery({
    ...publicSiteSettingsQuery(),
    // Seed from the loader so the first client render matches the server HTML.
    initialData: loaderData?.settings ?? undefined,
  });
  const clinicWhatsapp = settings?.clinic_whatsapp ?? "";
  const copy = LINKS_COPY[lang];
  const dir = lang === "en" ? "ltr" : "rtl";

  const whatsappHref = linksWhatsappHref(clinicWhatsapp, lang);

  const arrow = <ChevronRight className={`size-5 shrink-0 text-brown-500 ${lang === "ar" ? "rotate-180" : ""}`} strokeWidth={1.75} aria-hidden />;

  return (
    <div dir={dir} className="min-h-dvh bg-cream-200 text-brown-700">
      <div className="mx-auto flex max-w-md flex-col px-5 py-10">
        <LangToggle lang={lang} copy={copy} />

        {/* Header */}
        <header className="mt-6 flex flex-col items-center text-center">
          <div className="grid size-20 place-items-center overflow-hidden rounded-full border-2 border-solid border-modern-orange bg-cream-300">
            <img
              src={avatarAsset.url}
              alt={copy.headerName}
              width={80}
              height={80}
              className="h-full w-full object-cover"
            />
          </div>
          <h1 className="mt-4 font-display text-[26px] font-extrabold leading-tight">
            {copy.headerName}
          </h1>
          <p className="mt-1 font-display text-[15px] font-bold text-amber-800">
            {copy.headerRole}
          </p>
          <p className="mt-2 max-w-xs text-[15px] leading-relaxed text-brown-500">
            {copy.headerTagline}
          </p>
        </header>

        {/* Link rows */}
        <nav aria-label={lang === "ar" ? "روابط" : "Links"} className="mt-8 flex flex-col gap-2.5">
          {whatsappHref ? (
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className={rowCls}>
              <LinkRowBody icon={MessageCircle} tint="bg-amber-100 text-amber-800" label={copy.cards.consultation.label} description={copy.cards.consultation.description} arrow={arrow} />
            </a>
          ) : (
            <Link to="/book" className={rowCls}>
              <LinkRowBody icon={MessageCircle} tint="bg-amber-100 text-amber-800" label={copy.cards.consultation.label} description={copy.cards.consultation.description} arrow={arrow} />
            </Link>
          )}
          <Link to="/bousala" search={{ lang }} className={rowCls}>
            <LinkRowBody icon={Compass} tint="bg-green-100 text-green-800" label={copy.cards.bousala.label} description={copy.cards.bousala.description} arrow={arrow} />
          </Link>
          <Link to="/blog" className={rowCls}>
            <LinkRowBody icon={BookOpen} tint="bg-blue-100 text-blue-700" label={copy.cards.blog.label} description={copy.cards.blog.description} arrow={arrow} />
          </Link>
          <Link to="/" className={rowCls}>
            <LinkRowBody icon={Globe} tint="bg-cream-100 text-brown-700" label={copy.cards.website.label} description={copy.cards.website.description} arrow={arrow} />
          </Link>
        </nav>

        {/* Social icons */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          <SocialIcon href={SOCIAL.instagram} label={copy.socialAria.instagram} icon={Instagram} />
          <SocialIcon href={SOCIAL.tiktok} label={copy.socialAria.tiktok} icon={TikTokIcon} />
          <SocialIcon href={SOCIAL.youtube} label={copy.socialAria.youtube} icon={Youtube} />
          <SocialIcon href={SOCIAL.facebook} label={copy.socialAria.facebook} icon={Facebook} />
          <SocialIcon href={SOCIAL.threads} label={copy.socialAria.threads} icon={ThreadsIcon} />
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-[13px] text-brown-500" dir="ltr">
          {copy.domain}
        </p>
      </div>
    </div>
  );
}

const focusRing = "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-500/50";
const rowCls = `${card.interactive} flex min-h-14 items-center gap-3 px-4 py-3 ${focusRing}`;

function LinkRowBody({ icon: Icon, tint, label, description, arrow }: {
  icon: ComponentType<{ className?: string; strokeWidth?: number }>; tint: string; label: string; description: string; arrow: ReactNode;
}) {
  return (
    <>
      <span className={`grid size-10 shrink-0 place-items-center rounded-ds-sm ${tint}`}>
        <Icon className="size-5" strokeWidth={1.75} aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-display text-[17px] font-bold">{label}</span>
        <span className="block text-[13px] leading-snug text-brown-500">{description}</span>
      </span>
      {arrow}
    </>
  );
}

function LangToggle({ lang, copy }: { lang: LinksLang; copy: (typeof LINKS_COPY)[LinksLang] }) {
  const navigate = useNavigate();
  const set = (next: LinksLang) => {
    if (next === lang) return;
    navigate({ to: "/links", search: { lang: next }, replace: true });
  };
  const btn = (on: boolean) =>
    `min-h-12 min-w-12 rounded-ds-pill px-4 font-display text-[14px] font-bold transition-colors duration-ds-fast ${focusRing} ${on ? "bg-amber-500 text-brown-700" : "text-brown-500 hover:text-brown-700"}`;
  return (
    <div className="flex justify-end" role="group" aria-label={copy.langToggleAria}>
      <div className="inline-flex items-center rounded-ds-pill border border-hairline-default bg-surface p-0.5">
        <button type="button" onClick={() => set("ar")} aria-label={copy.langAriaAr} aria-pressed={lang === "ar"} className={btn(lang === "ar")}>AR</button>
        <button type="button" onClick={() => set("en")} aria-label={copy.langAriaEn} aria-pressed={lang === "en"} className={btn(lang === "en")}>EN</button>
      </div>
    </div>
  );
}

function SocialIcon({ href, label, icon: Icon }: { href: string; label: string; icon: ComponentType<{ className?: string; strokeWidth?: number }> }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className={`grid size-12 place-items-center rounded-full border border-hairline-default text-brown-700 transition-colors duration-ds-fast hover:bg-cream-100 ${focusRing}`}
    >
      <Icon className="size-5" strokeWidth={1.75} />
    </a>
  );
}

/**
 * Re-exported for tests — the prefilled WhatsApp message per language.
 */
export { LINKS_WHATSAPP_MESSAGE };
